import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight, Trash2, MapPin, Building2, Pencil, Upload, Search, Star, RefreshCw, Loader2, ArrowUpDown } from "lucide-react";
import api from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import Modal from "../components/Modal";
import SuspectEditor from "../components/SuspectEditor";
import SuspectPromoteDialog from "../components/SuspectPromoteDialog";
import SuspectImportDialog from "../components/SuspectImportDialog";
import { useAuth } from "../context/AuthContext";

const SORT_OPTIONS = [
  { id: "newest", label: "Newest", key: "created_at", dir: -1, type: "string" },
  { id: "oldest", label: "Oldest", key: "created_at", dir: 1, type: "string" },
  { id: "name_asc", label: "Name A→Z", key: "name", dir: 1, type: "string" },
  { id: "name_desc", label: "Name Z→A", key: "name", dir: -1, type: "string" },
  { id: "star_desc", label: "Star ↓", key: "star_rating", dir: -1, type: "number" },
  { id: "star_asc", label: "Star ↑", key: "star_rating", dir: 1, type: "number" },
  { id: "price_desc", label: "Price ↓", key: "_price_n", dir: -1, type: "number" },
  { id: "price_asc", label: "Price ↑", key: "_price_n", dir: 1, type: "number" },
  { id: "area", label: "Area", key: "province_area", dir: 1, type: "string" },
  { id: "rating_desc", label: "Google ↓", key: "google_rating", dir: -1, type: "number" },
];

// Extract a numeric "max R/night" so price sort works on heterogenous strings
// like "R5000-R8000" / "premium" / "< R500" / "R 12 000 / night".
function priceNumeric(s) {
  if (!s) return null;
  if (typeof s === "number") return s;
  const txt = String(s).replace(/\s/g, "");
  const ns = txt.match(/\d{2,6}/g);
  if (!ns) return null;
  return Math.max(...ns.map(Number));
}

/**
 * Suspects — earliest funnel stage, shown either via /leads?tab=suspects
 * (preferred entry) or this dedicated page (linked from Leads page CTA).
 *
 * Same SuspectEditor is used for create + edit. Promote is atomic
 * (creates Customer + Lead and migrates content).
 */
export default function Suspects() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null); // null | {} | suspect
  const [promote, setPromote] = useState(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [sortId, setSortId] = useState(() => localStorage.getItem("suspects_sort") || "newest");
  const [refreshingId, setRefreshingId] = useState("");
  const [bulkRefreshing, setBulkRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/suspects");
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => { load(); }, []);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  useAutoRefresh(load, { intervalMs: 30_000 });
  useEffect(() => { localStorage.setItem("suspects_sort", sortId); }, [sortId]);

  const remove = async (id) => {
    if (!window.confirm("Discard this suspect?")) return;
    await api.delete(`/suspects/${id}`);
    await load();
  };

  const refreshOne = async (id) => {
    setRefreshingId(id);
    try {
      await api.post(`/suspects/${id}/refresh-enrichment`);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshingId("");
    }
  };

  const refreshAllFailed = async () => {
    setBulkRefreshing(true);
    try {
      const { data } = await api.post(`/suspects/refresh-enrichment-bulk`, { only_failed: true });
      window.alert(`Re-enriched ${data?.processed || 0} suspect${(data?.processed || 0) === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Refresh failed.");
    } finally {
      setBulkRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((s) =>
        [s.name, s.phone, s.email, s.industry, s.province_area, s.estate_reserve, s.contact_name, s.notes, s.source]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    // Apply sort
    const opt = SORT_OPTIONS.find((o) => o.id === sortId) || SORT_OPTIONS[0];
    const enriched = list.map((s) => ({ ...s, _price_n: priceNumeric(s.price_per_night) }));
    enriched.sort((a, b) => {
      const av = a[opt.key];
      const bv = b[opt.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last
      if (bv == null) return -1;
      if (opt.type === "number") {
        return (Number(av) - Number(bv)) * opt.dir;
      }
      return String(av).localeCompare(String(bv)) * opt.dir;
    });
    return enriched;
  }, [items, search, sortId]);

  const isOwner = user?.role === "owner" || user?.role === "admin";

  return (
    <div className="space-y-4 hv-fade-in" data-testid="suspects-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Suspects</h1>
          <p className="text-sm text-[#a0aabf] mt-1 max-w-xl">
            The top of the funnel — names, photos, leads we know exist but haven't engaged yet. Every column
            from your prospect-log spreadsheet is captured here.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isOwner && (
            <button
              type="button"
              onClick={() => setImporting(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="suspects-import-btn"
            >
              <Upload className="w-3.5 h-3.5" /> Import xlsx
            </button>
          )}
          <button
            type="button"
            onClick={() => setEdit({})}
            className="hv-btn hv-btn-primary"
            data-testid="suspect-new-btn"
          >
            <Plus className="w-4 h-4" /> New suspect
          </button>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="hv-card p-2 flex items-center gap-2 flex-wrap">
        <Search className="w-4 h-4 text-[#647087] ml-1" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, phone, area, contact…"
          className="hv-input border-0 bg-transparent flex-1 min-w-[180px]"
          data-testid="suspect-search"
        />
        <div className="inline-flex items-center gap-1 text-xs text-[#a0aabf]" title="Sort suspects">
          <ArrowUpDown className="w-3.5 h-3.5" />
          <select
            value={sortId}
            onChange={(e) => setSortId(e.target.value)}
            className="hv-input py-1 px-2 text-xs"
            data-testid="suspect-sort-select"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={refreshAllFailed}
          disabled={bulkRefreshing}
          className="hv-btn hv-btn-ghost text-xs"
          title="Re-run enrichment for any suspect with missing fields"
          data-testid="suspect-bulk-refresh-btn"
        >
          {bulkRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {bulkRefreshing ? "Re-enriching…" : "Re-enrich incomplete"}
        </button>
        <span className="text-[10px] text-[#647087] font-mono">{filtered.length} / {items.length}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#a0aabf]" data-testid="suspects-empty">
          {items.length === 0 ? "No suspects yet — capture one or import your spreadsheet." : "No matches for that search."}
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="suspect-list">
          {filtered.map((s) => (
            <div
              key={s.id}
              className="hv-card p-3 flex items-start gap-3 hover:border-[#1FB6C1]/40 transition"
              data-testid={`suspect-row-${s.id}`}
            >
              {(() => {
                // [Iter 322] Show whichever cover-photo storage
                // shape exists — R2 upload_id wins over legacy base64.
                const src = s.photo_upload_id
                  ? `/api/uploads/${s.photo_upload_id}`
                  : s.photo_data_url;
                if (!src) return null;
                return (
                  <img
                    src={src}
                    alt={s.name}
                    className="w-12 h-12 rounded object-cover border border-white/10 shrink-0"
                  />
                );
              })()}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold truncate">{s.name}</span>
                  {s.industry && <span className="text-[10px] uppercase tracking-wider text-[#A78BFA]">{s.industry}</span>}
                  {s.star_rating && (
                    <span className="text-[10px] text-amber-300 inline-flex items-center gap-0.5">
                      <Star className="w-2.5 h-2.5 fill-current" /> {s.star_rating}
                    </span>
                  )}
                  {s.enrichment_status === "pending" && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA]" title="Enrichment running in background">
                      enriching…
                    </span>
                  )}
                  {s.enrichment_status === "failed" && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300" title="Enrichment failed — click refresh to retry">
                      enrich failed
                    </span>
                  )}
                  {s.enrichment_status === "partial" && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" title="Some sources couldn't be reached — click refresh to retry">
                      partial
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#a0aabf] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {s.contact_name && <span><strong>{s.contact_name}</strong>{s.contact_position ? ` (${s.contact_position})` : ""}</span>}
                  {s.phone && <span>{s.phone}</span>}
                  {s.email && <span className="truncate">{s.email}</span>}
                  {s.province_area && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{s.province_area}</span>}
                  {s.estate_reserve && <span className="inline-flex items-center gap-0.5"><Building2 className="w-3 h-3" />{s.estate_reserve}</span>}
                  {s.physical_address && !s.estate_reserve && <span className="truncate inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{s.physical_address}</span>}
                  {s.price_per_night && <span className="text-[#10B981]">{s.price_per_night}</span>}
                  {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-[#1FB6C1] underline truncate" onClick={(e) => e.stopPropagation()}>{(s.website || "").replace(/^https?:\/\//, "").slice(0, 30)}</a>}
                </div>
                {s.notes && <div className="text-xs text-[#647087] mt-1 line-clamp-2">{s.notes}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => refreshOne(s.id)}
                  disabled={refreshingId === s.id}
                  className="hv-btn hv-btn-ghost text-xs"
                  title="Re-run enrichment for this suspect"
                  data-testid={`suspect-refresh-${s.id}`}
                >
                  {refreshingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => setEdit(s)} className="hv-btn hv-btn-ghost text-xs" title="Edit" data-testid={`suspect-edit-${s.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setPromote(s)} className="hv-btn hv-btn-primary text-xs" title="Promote to customer + lead" data-testid={`suspect-promote-${s.id}`}>
                  <ArrowRight className="w-3.5 h-3.5" /> Promote
                </button>
                <button onClick={() => remove(s.id)} className="hv-btn hv-btn-ghost text-xs" title="Delete" data-testid={`suspect-delete-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-rose-300" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.id ? `Edit suspect — ${edit.name}` : "New suspect"}
        testId="suspect-modal"
        maxWidth="max-w-2xl"
      >
        {edit && (
          <SuspectEditor
            value={edit}
            onSaved={() => { setEdit(null); load(); }}
            onCancel={() => setEdit(null)}
          />
        )}
      </Modal>

      <Modal
        open={!!promote}
        onClose={() => setPromote(null)}
        title={`Promote ${promote?.name || ""}`}
        testId="suspect-promote-modal"
        maxWidth="max-w-2xl"
      >
        {promote && (
          <SuspectPromoteDialog
            suspect={promote}
            onPromoted={(d) => {
              setPromote(null);
              load();
              if (d?.lead?.id) navigate(`/leads/${d.lead.id}`);
            }}
            onCancel={() => setPromote(null)}
          />
        )}
      </Modal>

      <Modal
        open={importing}
        onClose={() => setImporting(false)}
        title="Import suspects from spreadsheet"
        testId="suspect-import-modal"
        maxWidth="max-w-3xl"
      >
        <SuspectImportDialog
          onDone={() => { setImporting(false); load(); }}
          onCancel={() => setImporting(false)}
        />
      </Modal>
    </div>
  );
}
